<template>
  <div class="ai-library-dialog" :class="{ open: showAiImagesLibrary }">
    <div class="ai-library-header">
      <h3>{{ $t('ספריית תמונות AI') }}</h3>
      <close-btn @click="close"></close-btn>
    </div>
    <div class="ai-library-body scrollable-y">
      <div v-if="imagesLibrary.length === 0" class="empty-library">
        <p>{{ $t('אין תמונות בספרייה עדיין') }}</p>
      </div>
      <div v-else class="images-grid">
        <div v-for="(image, index) in imagesLibrary" :key="index" class="image-item">
          <img :src="image" alt="AI Generated" @click="previewImage(image)" />
          <button class="delete-btn" @click.stop="removeImage(index)">
            <fp-icon symbol="trash"></fp-icon>
          </button>
        </div>
      </div>
    </div>

    <!-- Image Preview Overlay -->
    <div v-if="previewingImage" class="image-preview-overlay" @click="previewingImage = null">
      <div class="preview-container" @click.stop>
        <img :src="previewingImage" alt="Preview" />
        <close-btn class="preview-close" @click="previewingImage = null"></close-btn>
        <div class="preview-actions">
           <fp-icon-button symbol="download" @click="downloadImage(previewingImage)">{{ $t('הורדה') }}</fp-icon-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapGetters} from 'vuex';
import CloseBtn from "@/components/buttons/CloseBtn";
import FpIcon from "@/components/FpIcon";
import FpIconButton from "@/components/buttons/FpIconButton";

export default {
  name: 'AiImagesLibraryDialog',
  components: {
    CloseBtn,
    FpIcon,
    FpIconButton,
  },
  computed: {
    previewingImage: {
      get() {
        return this.$store.getters['aIAgent/previewingImage'];
      },
      set(value) {
        this.$store.commit('aIAgent/previewingImage', value);
      }
    },
    ...mapGetters('aIAgent', ['showAiImagesLibrary', 'imagesLibrary']),
  },
  methods: {
    close() {
      this.$store.commit('aIAgent/showAiImagesLibrary', false);
    },
    removeImage(index) {
      if (confirm(this.$t('האם אתה בטוח שברצונך למחוק תמונה זו?'))) {
        this.$store.dispatch('aIAgent/removeFromImagesLibrary', index);
      }
    },
    previewImage(image) {
      this.$store.commit('aIAgent/previewingImage', image);
    },
    downloadImage(dataUrl) {
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `ai-image-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  },
};
</script>

<style scoped lang="scss">
.ai-library-dialog {
  position: fixed;
  z-index: 1001;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90vw;
  max-width: 900px;
  height: 85vh;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.95) 0%, #000000 100%);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 20px;
  display: none;
  flex-direction: column;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.8);

  &.open {
    display: flex;
  }

  .ai-library-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    padding-bottom: 10px;

    h3 {
      margin: 0;
      color: white;
      font-size: 1.5rem;
    }
  }

  .ai-library-body {
    flex-grow: 1;
    overflow-y: auto;
    padding-right: 5px;

    &::-webkit-scrollbar {
      width: 6px;
    }
    &::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.2);
      border-radius: 3px;
    }
  }

  .empty-library {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    color: rgba(255, 255, 255, 0.5);
    font-size: 1.2rem;
  }

  .images-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 15px;
  }

  .image-item {
    position: relative;
    aspect-ratio: 1;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.1);
    cursor: pointer;
    transition: transform 0.2s ease;

    &:hover {
      transform: scale(1.02);
      .delete-btn {
        opacity: 1;
      }
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .delete-btn {
      position: absolute;
      top: 8px;
      right: 8px;
      background: rgba(255, 0, 0, 0.7);
      border: none;
      color: white;
      width: 30px;
      height: 30px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      opacity: 0;
      transition: opacity 0.2s ease;

      &:hover {
        background: rgba(255, 0, 0, 1);
      }
    }
  }

  .image-preview-overlay {
    position: fixed;
    top: 0;
    left: 50%;
    width: 90vw;
    height: 90vh;
    background: rgba(0, 0, 0, 0.9);
    z-index: 1002;
    display: flex;
    justify-content: center;
    align-items: center;
    transform: translateX(-50%);

    .preview-container {
      position: relative;
      max-width: 90vw;
      max-height: 90vh;
      display: flex;
      flex-direction: column;
      align-items: center;

      img {
        max-width: 100%;
        max-height: 80vh;
        border-radius: 8px;
        box-shadow: 0 0 20px rgba(0, 0, 0, 0.5);
      }

      .preview-close {
        position: absolute;
        top: -40px;
        right: 0;
      }

      .preview-actions {
        margin-top: 20px;
      }
    }
  }
}

@media (max-width: 768px) {
  .ai-library-dialog {
    width: 100vw;
    height: 100vh;
    border-radius: 0;
    top: 0;
    left: 0;
    transform: none;
  }
  
  .images-grid {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  }

  .image-item .delete-btn {
    opacity: 1;
  }
}
</style>
